import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { getHowToGuide } from '../../data/howToGuides';

// Inline "how do I actually do this" trigger. Wraps a short piece of text
// (e.g. "Insert -> Clustered Bar Chart") with a dotted underline + tap icon;
// clicking it opens a small dialog with the exact numbered clicks, pulled
// from the shared guide library (data/howToGuides.js) so a click path is
// written once and reused everywhere it comes up, instead of every lesson
// re-explaining the same menu path (or leaving it to a mentor to explain live).
//
// Usage: <HowToLink guideId="excel-insert-chart">Insert -> Clustered Bar Chart</HowToLink>
// If `guideId` has no matching guide, renders children unchanged (no dotted
// underline) so a typo in a guide id never silently looks interactive.
const HowToLink = ({ guideId, children, compact = false }) => {
  const guide = getHowToGuide(guideId);
  const [open, setOpen] = useState(false);

  if (!guide) return <>{children}</>;

  return (
    <>
      <Box
        component="span"
        role="button"
        aria-label={compact ? `Open help: ${guide.title}` : undefined}
        title={compact ? guide.title : undefined}
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(true);
          }
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: compact ? 0 : 0.3,
          width: compact ? 18 : 'auto',
          height: compact ? 18 : 'auto',
          ml: compact ? 0.5 : 0,
          borderRadius: compact ? '50%' : 0,
          border: compact ? '1px solid' : 'none',
          borderColor: compact ? 'currentColor' : 'transparent',
          color: 'primary.main',
          fontWeight: 600,
          fontSize: compact ? '0.72rem' : 'inherit',
          lineHeight: compact ? '18px' : 'inherit',
          textDecoration: compact ? 'none' : 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: '3px',
          cursor: 'pointer',
          verticalAlign: compact ? 'text-top' : 'baseline',
          '&:hover': {
            textDecorationStyle: 'solid',
            bgcolor: compact ? 'action.hover' : 'transparent',
          },
        }}
      >
        {compact ? <HelpOutlineIcon sx={{ fontSize: 13 }} /> : children}
        {!compact && <TouchAppIcon sx={{ fontSize: '0.9em', verticalAlign: 'middle' }} />}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, pb: 1 }}>
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1, letterSpacing: 1 }}>
              {guide.tool}
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.3 }}>
              {guide.title}
            </Typography>
          </Stack>
          <IconButton onClick={() => setOpen(false)} size="small" aria-label="Close" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.5,
              '& li': { mb: 1.1, lineHeight: 1.6, '&:last-child': { mb: 0 } },
            }}
          >
            {guide.steps.map((step, index) => (
              <Typography key={index} component="li" variant="body2">
                {step}
              </Typography>
            ))}
          </Box>
          {guide.tip && (
            <Box
              sx={{
                mt: 2,
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Tip:</strong> {guide.tip}
              </Typography>
            </Box>
          )}
          {guide.advanced && (
            <Box
              sx={{
                mt: 2,
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" fontWeight={700} color="text.secondary">
                {guide.advanced.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {guide.advanced.note}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Got it</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HowToLink;
